/**
 * Regional tracking permission for Google Ads conversion reporting.
 *
 * Chosen route: NO cookie banner. Conversion measurement therefore does not run
 * for visitors in regions that require prior consent (EEA, UK, Switzerland).
 * Quebec is handled by Google's own regional consent defaults in index.html
 * (region: 'CA-QC'), because a country-level lookup cannot see provinces —
 * listing all of Canada here would silently switch off tracking for every
 * Toronto/GTA visitor, which is exactly who the ads target.
 *
 * If the location lookup fails we do NOT block: personalization and user-data
 * signals are denied by default everywhere, and the gtag regional defaults
 * still deny storage in consent regions, so plain conversion counting is safe.
 */

const CONSENT_COUNTRIES = [
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU",
  "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES",
  "SE", "IS", "LI", "NO", "GB", "CH",
];

let consentRegion: Promise<boolean> | undefined;
let resolvedRequired: boolean | undefined;

export function isConsentRequiredRegion(): Promise<boolean> {
  return (consentRegion ??= lookupConsentRegion().then((r) => {
    resolvedRequired = r;
    return r;
  }));
}

/**
 * Synchronous read for code that cannot wait (form submit stamping).
 * Starts the lookup if needed; until it settles, treats the region as
 * not requiring consent (same as the lookup-failure fallback).
 */
export function canTrackAdsSync(): boolean {
  void isConsentRequiredRegion();
  return resolvedRequired !== true;
}

async function lookupConsentRegion(): Promise<boolean> {
  try {
    const res = await fetch("/cdn-cgi/trace", { signal: AbortSignal.timeout(2000) });
    if (!res.ok) return false;
    const country = (await res.text()).match(/^loc=([A-Z0-9]{2})$/m)?.[1];
    if (!country || country === "XX" || country === "T1") return false;
    return CONSENT_COUNTRIES.includes(country);
  } catch {
    // Unresolved region: rely on Google's regional consent defaults instead of
    // dropping the conversion entirely.
    return false;
  }
}

/** True where conversion counting may run without a recorded choice. */
export async function canTrackAds(): Promise<boolean> {
  return !(await isConsentRequiredRegion());
}
